import React from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import {
  ComparedMetricDistributionChart,
  deriveYesNoString,
  resolveSuccessValue,
  StringValueYesNoChip,
} from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"

import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { TargetCondition } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewString } from "../../components/aggregate/AggregateOverviewString"
import { BinaryCompareByPctChart } from "../../components/charts/BinaryCompareByPctChart"
import { SingleBinary } from "../../components/single/SingleBinary"

/**
 * Type base class for binary (YES/NO/UNKNOWN) string output metrics.
 *
 * Contains all output-format-specific rendering as conditional branches in MetricRecord.
 * No metric-specific logic here — extend this class for for it.
 */
export class BinaryMetricRecord extends MetricRecord {
  overviewSize = "small" as const
  aggregate_units = "%"
  invertYesNoColors = false

  /**
   * Target condition used for success coloring. When invertYesNoColors is set
   * (metrics where "NO" is the positive outcome, e.g. "Agent fails to
   * respond"), force a NO-target condition; otherwise fall back to the
   * metric's configured target condition.
   */
  getEffectiveTargetCondition(): TargetCondition | undefined {
    return this.invertYesNoColors
      ? { target_values: [ANNOTATION_VALUE.NO], comparison_operator: "in" }
      : this.metric_metadata?.target_condition
  }

  // ─── Comparison ──────────────────────────────────────────────────────────

  compareValues(
    value1: { float_value?: number | null; string_value?: string | null },
    value2: { float_value?: number | null; string_value?: string | null },
  ): boolean {
    return deriveYesNoString(value1) === deriveYesNoString(value2)
  }

  // ─── Badge ────────────────────────────────────────────────────────────────

  // Badge: Yes / No / Unknown (N/A)
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const effectiveTargetCondition: TargetCondition | undefined = this
      .invertYesNoColors
      ? { target_values: [ANNOTATION_VALUE.NO], comparison_operator: "in" }
      : undefined
    return (
      <StringValueYesNoChip
        mo={mo}
        targetCondition={effectiveTargetCondition}
        metric={this.invertYesNoColors ? undefined : this}
      />
    )
  }

  // ─── Single output ────────────────────────────────────────────────────────

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    const effectiveTargetCondition: TargetCondition | undefined = this
      .invertYesNoColors
      ? { target_values: [ANNOTATION_VALUE.NO], comparison_operator: "in" }
      : undefined
    return (
      <SingleBinary
        mo={mo}
        targetCondition={effectiveTargetCondition}
        metric={this.invertYesNoColors ? undefined : this}
      />
    )
  }

  // ─── Overview / run review ────────────────────────────────────────────────

  getOverviewNode(
    mo: RunMetricOutput[],
    controlledProps?: {
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode {
    for (const m of mo) {
      m.string_value = deriveYesNoString(m)
    }
    return (
      <AggregateOverviewString
        mos={mo}
        isBinary
        targetCondition={this.getEffectiveTargetCondition()}
        includeUnknowns={controlledProps?.includeUnknowns}
        onIncludeUnknownsChange={controlledProps?.onIncludeUnknownsChange}
        hideCheckbox={controlledProps?.hideCheckbox}
      />
    )
  }

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const successValue = resolveSuccessValue(this, this.invertYesNoColors)
    const data = allGroupKeys.map((key) => {
      const groupMo = groups.get(key) ?? []
      const total = groupMo.length
      const successCount = groupMo.filter(
        (m) => deriveYesNoString(m) === successValue,
      ).length
      const pct = total > 0 ? (successCount / total) * 100 : 0
      return {
        label: getCompareByDisplayLabel(key, compareBy),
        value: pct,
        color: compareBy.colors[key],
      }
    })
    return (
      <BinaryCompareByPctChart
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        data={data}
        valueLabel="Success %"
      />
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    if (!mo[0]) {
      return null
    }

    const hasUnknowns = mo.some(
      (m) => m.string_value?.trim() === ANNOTATION_VALUE.UNKNOWN,
    )
    if (hasUnknowns) {
      return null
    }

    const valid = mo.filter(
      (m) =>
        m.string_value === ANNOTATION_VALUE.YES ||
        m.string_value === ANNOTATION_VALUE.NO,
    )
    if (valid.length === 0) {
      return null
    }

    const successValue = resolveSuccessValue(this, this.invertYesNoColors)

    const successCount = valid.filter(
      (m) => m.string_value === successValue,
    ).length
    const percentage = (successCount / valid.length) * 100
    return `${percentage.toFixed(0)}%`
  }

  // Failure rate for "YES = bad" metrics: % of outputs that are NOT the success
  // value (0% = healthy). deriveYesNoString lets float-output rows (null
  // string_value) count, consistent with getOverviewNode.
  protected getFailureRateHeaderValue(mo: RunMetricOutput[]): string | null {
    const labels = mo
      .map((m) => deriveYesNoString(m))
      .filter((v) => v === ANNOTATION_VALUE.YES || v === ANNOTATION_VALUE.NO)
    if (labels.length === 0) {
      return null
    }
    const successValue = resolveSuccessValue(this, this.invertYesNoColors)
    const failureCount = labels.filter((v) => v !== successValue).length
    return `${((failureCount / labels.length) * 100).toFixed(0)}%`
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={metricOutputs}
          isBinary
          targetCondition={this.getEffectiveTargetCondition()}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  renderComparedInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    // Ensure string_value is populated from deriveYesNoString for legacy metric outputs
    // that may only have float_value, and force output_type to "string" for the chart.
    const normalizedOutputs = metricOutputs.map((m) => ({
      ...m,
      string_value: deriveYesNoString(m),
      output_type: "string" as const,
    }))
    const successValue = resolveSuccessValue(this, this.invertYesNoColors)
    return (
      <ComparedMetricDistributionChart
        metricOutputs={normalizedOutputs}
        runRows={runRows}
        metricRecord={this}
        compareBy={compareBy}
        successValue={successValue}
      />
    )
  }

  // ─── Alert / filter forms ─────────────────────────────────────────────────

  hasUnknowns(mo: RunMetricOutput[]): boolean {
    return mo.some((m) => m.string_value?.trim() === ANNOTATION_VALUE.UNKNOWN)
  }

  /**
   * Provide numeric values for grouped aggregation modes (avg / median / p95 / min / max).
   * For binary metrics we treat the "success" value as 100 and all other
   * non-unknown values as 0, so:
   * - Average gives the success rate in percent.
   * - P95 / median / min / max give 0% or 100% depending on distribution.
   */
  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    const successValue = resolveSuccessValue(this, this.invertYesNoColors)

    return metricOutputs
      .map((m) => {
        const value = deriveYesNoString(m)
        if (value === ANNOTATION_VALUE.UNKNOWN) {
          return null
        }
        return value === successValue ? 100 : 0
      })
      .filter((v): v is 0 | 100 => v !== null)
  }
}
